import React, { FC, useEffect, useRef, useState } from 'react'
import { ReactSVG } from 'react-svg'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { setPath, setUrlParam } from '../../../utils/url'
import { URL } from '../../constants/url'
import { Link, useHistory, useParams } from 'react-router-dom'
import { endOfWeek, format, getYear, nowDate, prevMonth } from '../../../utils/date'
import { useTranslation } from 'react-i18next'
import { fetchReportList, updateReadReport } from '../../../states/containers/report/reportSlice'
import { isManager } from '../../../utils/role'

interface Tip {
  display: boolean
  type: string
  team_id: number
  member_id: number
}

interface TipStyle {
  top?: number
  bottom?: number
  left: number
}

interface ReportList {
  date: string
  total_evaluation: string
  weekly_reports: {
    date: string
    is_read: boolean
  }[]
  is_read: boolean
}

type LangValue = 'ja' | 'en'

interface Props {
  isOpen: boolean
  toggleCallback: VoidFunction
}

const SideBarSub: FC<Props> = (props) => {
  const {
    isOpen,
    toggleCallback,
  } = props

  const {
    report,
    commonPart,
    login,
  } = useSelector((state: RootState) => state)

  const DEFAULT_OPEN_LIMIT = 10
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const dispatch = useDispatch()

  const { t } = useTranslation()

  const tipInit = {
    display: false,
    type: "",
    team_id: 0,
    member_id: 0,
  }

  const [tip, setTip] = useState<Tip>(tipInit)

  const [startDate, setStartDate] = useState<string>()

  const [targetYear, setTargetYear] = useState<number>(Number(`${format(prevMonth(nowDate()), "YYYY")}`))

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const [checkedTeamList, setCheckedTeamList] = useState<number[]>([])

  const [reportList, setReportList] = useState<ReportList>()

  const [tipStyle, setTipStyle] = useState<TipStyle>({ top: 0, left: 0 })

  const [id, setId] = useState<string>("")

  const [target, setTarget] = useState<string>("")

  const Ref = useRef<HTMLElement>(null)

  const buildSVG = (isUpgrade: boolean, iconFileName: string) => {
    const filePath = `/static/images/icon/${iconFileName}.svg`
    return (
      <ReactSVG
        src={filePath}
        className={classNames('icon', {
          isUpgrade: isUpgrade,
        })}
        wrapper="span"
      />
    )
  }

  const hoverElement = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, team_id: number, member_id?: number) => {
    const pageX = 290
    const pageY = event.currentTarget.offsetTop - (Ref.current?.scrollTop || 0)
    setTip({
      display: true,
      type: member_id ? "member" : "team",
      team_id: team_id,
      member_id: member_id || 0,
    })
    if (member_id) {
      setReportList(report.reportList?.teams.find((value) => value.id === team_id)?.members.find((value) => value.id === member_id)?.reports)
    } else {
      setReportList(report.reportList?.teams.find((value) => value.id === team_id)?.reports)
    }
    if ((window.innerHeight / 2) > pageY) {
      setTipStyle(
        {
          top: pageY,
          left: pageX,
        },
      )
    } else {
      setTipStyle(
        {
          bottom: (Ref.current?.clientHeight || 0) - pageY,
          left: pageX,
        },
      )
    }
  }

  const handleChangeMonth = (value: string) => {
    setIsCalendarDisplay(false)
    setStartDate(format(value))
    dispatch(fetchReportList({ target_date: value }))
  }

  const clickTeam = (team_id: number) => {
    if (checkedTeamList.includes(team_id)) {
      setCheckedTeamList(checkedTeamList.filter((v) => v !== team_id))
    } else {
      setCheckedTeamList([...checkedTeamList, team_id])
    }
  }

  const clickLink = (range: string, date?: string) => {
    let targetDate = format(startDate, "YYYY-MM-DD") || nowDate("YYYY-MM-DD")
    if (date) {
      targetDate = format(date, "YYYY-MM-DD")
    }
    const target_id = tip.type === "member" ? tip.member_id : tip.team_id

    history.push(`${setPath(setUrlParam(
      URL.REPORT_DETAIL.path,
      [
        { key: 'range', value: range },
        { key: 'target', value: tip.type },
        { key: 'id', value: target_id.toString() },
      ],
    ), language)}?date=${targetDate}`)
    setTip({ ...tip, display: false })
    dispatch(updateReadReport({ id: target_id, target: tip.type, type: range, date: targetDate }))
  }

  useEffect(() => {
    if (!report.teamReportList?.teams) return
    setCheckedTeamList(report.teamReportList?.teams.map((v) => v.id))
  }, [report.teamReportList?.teams])

  useEffect(() => {
    if (!(location.pathname.match("/report/monthly/") || location.pathname.match("/report/weekly/"))) {
      setId("")
      setTarget("")
      return
    }
    const pathArray = location.pathname.split("/")
    setId(pathArray[pathArray.length - 1])
    setTarget(pathArray[pathArray.length - 2])
  }, [location.pathname])

  useEffect(() => {
    setStartDate(Number(nowDate("DD")) <= 8 ? prevMonth(nowDate(), "YYYY-MM-DD") : nowDate("YYYY-MM-DD"))
  }, [])

  return (
    <>
      {
        location.pathname.match(URL.REPORT.path) && isManager(login.role) ? (
          <div className="side_sub_wrap">
            <div className="tools">
              <div className="calendar_text">
                <button
                  type="button"
                  className={`button ${isOpen && "display"}`}
                  onClick={() => setIsCalendarDisplay(true)}
                >
                  {format(startDate, "YYYY年MM月")}
                  <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
                </button>
                <button
                  type="button"
                  className={`menu_toggle ${isOpen && "display"}`}
                  onClick={() => toggleCallback()}
                >
                  <div className={`menu ${isOpen && "active"}`}>
                    <FontAwesomeIcon icon={['fas', 'angle-double-left']} />
                  </div>
                </button>
              </div>
            </div>
            <section className="side_sub" ref={Ref}>
              <div>
                <Link
                  to={setPath(URL.REPORT.path, language)}
                  className={classNames({
                    active: location.pathname.match(URL.REPORT.path) && location.pathname.split("/").length === 3,
                    item: true,
                  })}
                >
                  <div className="name_label">
                    <div className="name_area">
                      <FontAwesomeIcon
                        icon={['fas', 'file-alt']}
                        className="icon task"
                      />
                      レポート一覧
                    </div>
                  </div>
                </Link>
                <Link
                  to={setPath(URL.REPORT_SEARCH.path, language)}
                  className={classNames({
                    active: location.pathname.match(URL.REPORT_SEARCH.path),
                    item: true,
                  })}
                >
                  <div className="name_label">
                    <div className="name_area">
                      <FontAwesomeIcon
                        icon={['fas', 'search']}
                        className="icon task"
                      />
                      検索
                    </div>
                  </div>
                </Link>
              </div>
              <div>
                {
                  report.reportList?.teams.map((team) => (
                    <React.Fragment key={`team${team.id}`}>
                      <div>
                        <div
                          className={`item ${
                            (
                              (
                                tip.type === "team"
                                && tip.team_id === team.id
                                && tip.display
                              ) || (
                                target === "team"
                                && id === team.id.toString()
                              )
                            ) && "active"
                          }`}
                          onMouseOver={(event) => hoverElement(event, team.id)}
                          onFocus={() => { /**/ }}
                          onMouseLeave={() => setTip({ ...tip, display: false })}
                          title={`クリックで${team.name}のユーザーを開く`}
                        >
                          <label htmlFor={`team${team.id}`}>
                            <FontAwesomeIcon
                              icon={['fas', 'angle-down']}
                              className={`arrow_icon ${checkedTeamList.includes(team.id) && 'open'}`}
                            />
                            <div className="name_area no_trans">
                              {buildSVG(false, "icon-team")}
                              {team.name}
                            </div>
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          id={`team${team.id}`}
                          className="toggle"
                          onClick={() => clickTeam(team.id)}
                          defaultChecked={(commonPart.members?.length || 0) < DEFAULT_OPEN_LIMIT}
                        />
                        <div>
                          {
                            team.members.map((member) => (
                              <div
                                key={`team${team.id}_member${member.id}`}
                                className={`item member ${
                                  (
                                    (
                                      tip.type === "member"
                                      && tip.team_id === team.id
                                      && tip.member_id === member.id && tip.display
                                    ) || (
                                      target === "member" && id === member.id.toString()
                                    )
                                  ) && "active"
                                }`}
                                onMouseOver={(event) => hoverElement(event, team.id, member.id)}
                                onFocus={() => { /**/ }}
                                onMouseLeave={() => setTip({ ...tip, display: false })}
                              >
                                <div className="name_label">
                                  <div className="name_area no_trans">
                                    <img
                                      alt=""
                                      src={member.image_url || `/static/images/profile/${member.id % 12 || 0}.jpeg`}
                                      className="icon"
                                    />
                                    {member.name}
                                  </div>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </React.Fragment>
                  ))
                }
              </div>
            </section>
            <div
              className={`side_sub_tip ${tip.display && "display"}`}
              style={tipStyle}
              onMouseOver={() => setTip({ ...tip, display: true, team_id: tip.team_id, member_id: tip.member_id })}
              onFocus={() => { /**/ }}
              onMouseLeave={() => setTip({ ...tip, display: false, team_id: 0, member_id: 0 })}
            >
              {
                reportList && reportList.total_evaluation && (
                  <>
                    <div className="title">
                      {t('report.list.box.monthly_report')}
                    </div>
                    <div
                      data-gtm-click={`${t('report.list.box.monthly_report')} - link`}
                      className="report_list_row"
                      role="none"
                      onClick={() => clickLink('monthly')}
                    >
                      <div className="text">
                        <div>
                          {format(reportList.date, "M月度")}
                          {t('report.list.box.monthly_report')}
                        </div>
                        {
                          reportList.is_read ? (
                            <></>
                          ) : (
                            <div className="new">
                              New
                            </div>
                          )
                        }
                      </div>
                      <div className="icon">
                        <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                      </div>
                    </div>
                  </>
                )
              }
              {
                reportList && reportList.weekly_reports && (
                  <div className="title">
                    週間業務レポート
                  </div>
                )
              }
              {
                reportList && reportList.weekly_reports.map((weekly_report) => (
                  <div
                    data-gtm-click={`${t('report.list.box.weekly_report')} - link`}
                    className="report_list_row"
                    key={`weekly_report_${weekly_report.date}`}
                    role="none"
                    onClick={() => clickLink('weekly', weekly_report.date)}
                  >
                    <div className="text">
                      <div>
                        {format(weekly_report.date, "M/D")}
                        ~
                        {format(endOfWeek(weekly_report.date), "M/D")}
                      </div>
                      {
                        weekly_report.is_read ? (
                          <></>
                        ) : (
                          <div className="new">
                            New
                          </div>
                        )
                      }
                    </div>
                    <div className="icon">
                      <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                    </div>
                  </div>
                ))
              }
              {
                (!reportList || !reportList.weekly_reports) && (
                  <div className="no_contents">
                    表示可能なレポートがありません
                  </div>
                )
              }
            </div>
            <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
              <div className="range">
                <div className="text">
                  {t(`range.calendar.header_text.monthly`)}
                </div>
              </div>
              <div className="monthly">
                <div className="select_month">
                  <div>
                    <button
                      type="button"
                      onClick={() => setTargetYear(targetYear - 1)}
                      disabled={Number(format(commonPart.company?.created_at, 'YYYY')) >= targetYear}
                    >
                      <FontAwesomeIcon icon={['fas', 'angle-left']} />
                    </button>
                  </div>
                  <div>
                    {`${targetYear}${t('time.time.short.year')}`}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setTargetYear(targetYear + 1)}
                      disabled={getYear() <= targetYear}
                    >
                      <FontAwesomeIcon icon={['fas', 'angle-right']} />
                    </button>
                  </div>
                </div>
                {
                  month.map((v, i) => {
                    const targetYearDate = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
                    const isClickableMonth = Number(nowDate('YYYYMM')) >= Number(targetYearDate)
                      && Number(format(commonPart.company?.created_at, 'YYYYMM')) <= Number(targetYearDate)
                    const isSelected = format(startDate, 'YYYYMM') === targetYearDate
                    return (
                      <div className="monthly_button" key={v}>
                        <button
                          type="button"
                          className={isSelected ? "selected" : ""}
                          onClick={() => handleChangeMonth(`${String(targetYear)}-${(`0${String(i + 1)}`).slice(-2)}-01`)}
                          disabled={!isClickableMonth}
                        >
                          {`${v}${t('time.time.short.month')}`}
                        </button>
                      </div>
                    )
                  })
                }
              </div>
            </div>
            <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
          </div>
        ) : (
          <>
          </>
        )
      }
    </>
  )
}

export default SideBarSub
